import { useCallback, useEffect, useState, useRef } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useSearchParams } from 'react-router-dom';
import { Spin, Form, Input } from 'antd';
import {
  createPlaidToken,
  createIdentityVerificationLinkToken,
  addPlaidIdentityVerification,
  addIdentityMatch,
  getIDVAuth,
  getOrganizationSettings,
  createIDVCustomer,
  createDwollaContact,
  exchangePublicToken,
  getOrganizationSettingsName,
} from 'services/api';
import {
  addPlaidLinkCallbackOnSuccess,
  addPlaidLinkCallbackOnEvent,
  addPlaidLinkCallbackOnExit,
} from 'modules/helpers';
import BrokersafeLogo from 'assets/icons/brokersafe_logo.svg';
import Button from 'components/Button';
import { OrganizationSetting } from 'routes/plaid/types';
import { Root } from './styles';
import { Customer } from './types';

const IdentityVerification = () => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [identityVerificationLinkToken, setIdentityVerificationLinkToken] =
    useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer>();
  const [idVerificationComplete, setIDVerificationComplete] = useState(false);
  const [isComplete, setComplete] = useState(false);
  const [isIDVSubmitting, setIDVSubmitting] = useState(false);
  const [iDVResult, setIDVResult] = useState({} as OrganizationSetting);
  const [isBankLinkSubmitting, setBankLinkSubmitting] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const idvPk = useRef<number | undefined>();
  const customerId = useRef<number | undefined>();
  const [idvCompleteSettings, setIDVCompleteSettings] = useState<
    OrganizationSetting[]
  >([]);
  const userPk = searchParams.get('id');
  const invitePk = searchParams.get('invite');

  const validateID = useCallback(async () => {
    if (!userPk) {
      setLoading(false);
      return;
    }

    try {
      const getAuthRes = await getIDVAuth(userPk, invitePk);
      if (!getAuthRes || !getAuthRes.Plaid_Identity_Verification_Template_ID) {
        setLoading(false);
        return;
      }

      const idvCompleteSettingsRes = await getOrganizationSettings(
        getAuthRes.Organization_PK,
        'Contact IDV',
        'Completed',
      );
      setIDVCompleteSettings(idvCompleteSettingsRes);

      setCustomer(getAuthRes);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [invitePk, userPk]);

  useEffect(() => {
    validateID();
  }, [validateID]);

  const { open: openBankLink, ready: isBankLinkReady } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      try {
        const tokenRes = await exchangePublicToken({
          public_token,
          metadata,
          customer: {
            ...customer,
            Customer_ID: customerId.current,
          },
        });
        setComplete(true);
        setBankLinkSubmitting(false);
        if (idvPk.current) {
          addIdentityMatch(tokenRes.plaidAccessToken, idvPk.current);
        }
        addPlaidLinkCallbackOnSuccess(customer, metadata);
      } catch (error) {
        setBankLinkSubmitting(false);
      }
    },
    onExit: (error, metadata: any) => {
      setBankLinkSubmitting(false);
      addPlaidLinkCallbackOnExit(customer, error, metadata);
    },
    onEvent: (eventName, metadata: any) => {
      addPlaidLinkCallbackOnEvent(customer, metadata);
    },
  });

  const { open: openIDV, ready: readyIDV } = usePlaidLink({
    token: identityVerificationLinkToken,
    onSuccess: async (_, metadata) => {
      try {
        const resIdvData = await addPlaidIdentityVerification({
          customer,
          identity_verification_id: metadata.link_session_id,
        });

        const resOrgSettingName = await getOrganizationSettingsName(
          customer?.Organization_PK!,
          resIdvData.status === 'success'
            ? 'IDV Completed - Success'
            : 'IDV Completed - Fail',
        );
        setIDVResult(resOrgSettingName);

        idvPk.current = resIdvData.Plaid_Identity_Verification_PK;
        const createDwollaContactRes = await createDwollaContact(
          customer!.Customer_PK,
        );
        customerId.current = createDwollaContactRes.dwolla_customer_id;
        setIDVerificationComplete(true);
        setIDVSubmitting(false);
        addPlaidLinkCallbackOnSuccess(customer, metadata);
      } catch (error) {
        setIDVSubmitting(false);
      }
    },
    onExit: (error, metadata: any) => {
      setIDVSubmitting(false);
      addPlaidLinkCallbackOnExit(customer, error, metadata);
    },

    onEvent: (eventName, metadata: any) => {
      addPlaidLinkCallbackOnEvent(customer, metadata);
    },
  });

  useEffect(() => {
    if (readyIDV) {
      openIDV();
    }
  }, [readyIDV, openIDV]);

  const renderBody = () => {
    if (!customer || !userPk) {
      return <p>We're sorry but the ID provided is not valid.</p>;
    }

    if (
      customer.Plaid_Identity_Verification_Template_ID &&
      !idVerificationComplete
    ) {
      return (
        <Spin
          style={{ width: '100%', marginTop: 150 }}
          tip="One moment while we start the identity verification process."
          size="large"
          spinning={isIDVSubmitting}
        >
          <p>{customer.Page_Content}</p>
          <Form
            className="idv-start-form"
            name="nest-messages"
            initialValues={{ email: customer.Customer_Email }}
            onFinish={async (values) => {
              setIDVSubmitting(true);

              const createIDVCustomerRes = await createIDVCustomer(
                customer.Organization_PK,
                userPk!,
                invitePk,
              );
              const additionalCustomer = {
                Customer_PK: createIDVCustomerRes.Customer_PK,
                Customer_ID: createIDVCustomerRes.Customer_PK,
                Customer_Email: values.email,
              };

              setCustomer((previous) => ({
                ...previous!,
                ...additionalCustomer,
              }));

              const idvLinkTokenRes = await createIdentityVerificationLinkToken(
                {
                  ...customer,
                  ...additionalCustomer,
                },
              );
              setIdentityVerificationLinkToken(idvLinkTokenRes.linkToken);

              const linkTokenRes = await createPlaidToken({
                ...customer,
                ...additionalCustomer,
              });
              setLinkToken(linkTokenRes.linkToken);
            }}
          >
            <Form.Item
              name="email"
              label=""
              rules={[
                {
                  required: true,
                  message: 'Required',
                },
                {
                  pattern: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                  message: 'Invalid email format',
                },
              ]}
            >
              <Input placeholder="Enter Email Address" />
            </Form.Item>
            <Form.Item className="submit-button-wrapper">
              <Button type="primary" htmlType="submit" disabled={!customer}>
                Start Identity Verification
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      );
    }

    if (!isComplete) {
      const failSetting = idvCompleteSettings?.find(
        (setting) => setting.Settings_Name === 'IDV Completed - Fail',
      );
      if (
        iDVResult?.Settings_Name === 'IDV Completed - Fail' &&
        failSetting?.Settings_IDV_Stop_On_Fail_Flag
      ) {
        return <p>{iDVResult.Settings_String}</p>;
      }

      return (
        <Spin
          style={{ width: '100%', marginTop: 150 }}
          tip="One moment while we complete the bank connect process."
          size="large"
          spinning={isBankLinkSubmitting}
        >
          <div className="bank-connect-section">
            <p>{iDVResult.Settings_String}</p>
            <Button
              onClick={() => {
                setBankLinkSubmitting(true);
                openBankLink();
              }}
              disabled={!isBankLinkReady}
            >
              Connect my Bank Account
            </Button>
          </div>
        </Spin>
      );
    }

    return (
      <p>
        Congratulations! Your bank account has been succesfully connected. You
        are now ready to receive payments.
      </p>
    );
  };

  if (isLoading) {
    return null;
  }

  return (
    <Root>
      <header className="header">
        {customer?.Organization_Logo_URL && (
          <img src={customer?.Organization_Logo_URL} alt="brokersafe-logo" />
        )}
      </header>
      <div className="container">{renderBody()}</div>
      <footer>
        <div className="tifle-logo-wrapper">
          <div>Powered By</div>
          <img src={BrokersafeLogo} alt="brokersafe-logo" />
        </div>
      </footer>
    </Root>
  );
};

export default IdentityVerification;
